import { useState, useEffect, useCallback, useRef } from 'react';

export type HeadingState = 'idle' | 'requesting' | 'active' | 'denied' | 'unsupported' | 'error';

/** Which orientation data source is currently providing heading values. */
export type HeadingSource =
  | 'webkit-compass'   // iOS Safari webkitCompassHeading (absolute, true compass north)
  | 'absolute-event'   // Android Chrome deviceorientationabsolute event
  | 'absolute-flag'    // deviceorientation with e.absolute === true
  | 'relative-alpha'   // deviceorientation relative alpha (last-resort fallback, may drift)
  | 'none';

/**
 * DeviceOrientationEvent with absolute-orientation fields.
 * Android Chrome fires `deviceorientationabsolute` which is a plain
 * DeviceOrientationEvent but with `absolute` implicitly true.
 */
type AbsoluteDeviceOrientationEvent = DeviceOrientationEvent & {
  absolute?: boolean;
  webkitCompassHeading?: number;
};

/**
 * DeviceOrientation hook for compass heading.
 *
 * Source priority (highest wins):
 *  1. `webkitCompassHeading` — iOS Safari/WebKit (absolute, true compass north).
 *  2. `deviceorientationabsolute` event — Android Chrome 50+ (absolute).
 *  3. `deviceorientation` with `e.absolute === true` — absolute flag.
 *  4. `deviceorientation` relative `alpha` — last-resort fallback; may drift
 *     from true north because alpha is relative to the device's starting
 *     orientation.
 *
 * Handles iOS 13+ permission prompt (DeviceOrientationEvent.requestPermission),
 * which must be called from a user gesture.
 * Applies low-pass filtering to reduce compass jitter.
 */
export function useHeading() {
  const [state, setState] = useState<HeadingState>('idle');
  const [heading, setHeading] = useState<number | null>(null);
  const [source, setSource] = useState<HeadingSource>('none');
  const [error, setError] = useState<string | null>(null);
  const smoothedRef = useRef<number | null>(null);
  const handlerRef = useRef<((e: AbsoluteDeviceOrientationEvent) => void) | null>(null);
  const usingAbsoluteEventRef = useRef(false);

  const stop = useCallback(() => {
    if (handlerRef.current) {
      window.removeEventListener('deviceorientationabsolute', handlerRef.current as EventListener);
      if (!usingAbsoluteEventRef.current) {
        window.removeEventListener('deviceorientation', handlerRef.current as EventListener, true);
      }
      handlerRef.current = null;
    }
    usingAbsoluteEventRef.current = false;
    setState('idle');
    setHeading(null);
    setSource('none');
    smoothedRef.current = null;
  }, []);

  const start = useCallback(async () => {
    // iOS 13+ requires explicit permission, invoked from a user gesture
    const dto = window.DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<string>;
    };
    if (dto?.requestPermission) {
      setState('requesting');
      try {
        const result = await dto.requestPermission();
        if (result !== 'granted') {
          setState('denied');
          setError('Motion permission denied. Enable Motion & Orientation in browser settings to use the compass.');
          return;
        }
      } catch {
        setState('denied');
        setError('Motion permission denied. Enable Motion & Orientation in browser settings to use the compass.');
        return;
      }
    }

    if (!('DeviceOrientationEvent' in window)) {
      setState('unsupported');
      setError('Device orientation is not supported by this browser.');
      return;
    }

    setState('active');
    setError(null);

    const handler = (e: AbsoluteDeviceOrientationEvent) => {
      // 1. iOS: webkitCompassHeading is the absolute compass heading (0-359.99)
      const compassHeading = e.webkitCompassHeading;
      let raw: number | null = null;
      let nextSource: HeadingSource = 'none';

      if (typeof compassHeading === 'number') {
        raw = compassHeading;
        nextSource = 'webkit-compass';
      } else if (e.absolute === true && e.alpha !== null) {
        // 3. deviceorientation with absolute flag — alpha is true compass heading
        raw = 360 - e.alpha;
        nextSource = 'absolute-flag';
      } else if (e.alpha !== null) {
        // 4. Relative alpha fallback — may drift from true north
        raw = 360 - e.alpha;
        nextSource = 'relative-alpha';
      }

      if (raw === null) return;

      // Update source once (first valid event determines the active source)
      if (source === 'none') {
        setSource(nextSource);
      }

      // Normalize to 0-360
      raw = ((raw % 360) + 360) % 360;

      // Low-pass filter to reduce jitter (alpha = 0.15 = smooth)
      if (smoothedRef.current === null) {
        smoothedRef.current = raw;
      } else {
        let diff = raw - smoothedRef.current;
        if (diff > 180) diff -= 360;
        if (diff < -180) diff += 360;
        smoothedRef.current = ((smoothedRef.current + diff * 0.15) % 360 + 360) % 360;
      }
      setHeading(smoothedRef.current);
    };

    handlerRef.current = handler;

    // 2. Android Chrome 50+ fires `deviceorientationabsolute` with true compass heading
    //    The event is a DeviceOrientationEvent with absolute implicitly true.
    //    If the browser fires this event, prefer it over the generic deviceorientation.
    const hasAbsoluteEvent = 'ondeviceorientationabsolute' in window;
    if (hasAbsoluteEvent) {
      usingAbsoluteEventRef.current = true;
      // Set source to absolute-event (will be confirmed on first event)
      setSource('absolute-event');
      window.addEventListener('deviceorientationabsolute', handler as EventListener);
    } else {
      usingAbsoluteEventRef.current = false;
      window.addEventListener('deviceorientation', handler as EventListener, true);
    }
  }, [source]);

  useEffect(() => {
    return () => {
      if (handlerRef.current) {
        window.removeEventListener('deviceorientationabsolute', handlerRef.current as EventListener);
        if (!usingAbsoluteEventRef.current) {
          window.removeEventListener('deviceorientation', handlerRef.current as EventListener, true);
        }
      }
    };
  }, []);

  return { state, heading, source, error, start, stop };
}
